import React, { useState, useEffect, useContext } from 'react';
import {
  Box, FormControl, FormLabel, Input, Button, Text, Heading, useToast,
  Alert, AlertDescription, AlertIcon, Icon, HStack
} from '@chakra-ui/react';
import { formatCurrencyAmount, ConfigContext } from './utils/Config';
import { useTranslation } from 'react-i18next';
import { FaLaptop } from 'react-icons/fa';

const MyCredit = ({ accessToken, userUuid, saleUuid, currency }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [creditInfo, setCreditInfo] = useState(null);
  const [bidderInfo, setBidderInfo] = useState(null);
  const [amountInput, setAmountInput] = useState(creditInfo?.amount || '');
  const [approvedAmount, setApprovedAmount] = useState(false);
  const config = useContext(ConfigContext);
  const isAmountChanged = amountInput !== (creditInfo?.amount || '');

  // Fetch bidder id info.
  useEffect(() => {
    const fetchCreditInfo = async () => {
      try {
        const response = await fetch(`${config.backendUrl}/api/v1.0/bidder_ids?filter[bidder_type]=website&user_uuid=${userUuid}&sale_uuid=${saleUuid}&access_token=${accessToken}`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (data.count === 0) {
          return;
        }
        setBidderInfo(data.data[0]);
        const liveCredit = data.data[0].live_credit;
        setApprovedAmount(liveCredit);
      } catch (error) {
        toast({
          title: t('myCredit.error'),
          description: error.title,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCreditInfo();
  }, [accessToken, userUuid, saleUuid, toast, config, t]);

  // Fetch credit info.
  useEffect(() => {
    const fetchCreditInfo = async () => {
      if (!bidderInfo) {
        return;
      }
      try {
        const response = await fetch(`${config.backendUrl}/api/credit_requests?sort=-id&user_uuid=${userUuid}&sale_uuid=${saleUuid}&access_token=${accessToken}`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (data.count === 0) {
          return;
        }
        const amount = data.data[0].amount;
        setCreditInfo(data.data[0]);
        setAmountInput(amount);
      } catch (error) {
        toast({
          title: t('myCredit.error'),
          description: error.title,
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCreditInfo();
  }, [accessToken, userUuid, saleUuid, toast, config, bidderInfo, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    let creditRequestData = {
      amount: amountInput,
      sale_uuid: saleUuid,
      user_uuid: userUuid
    };
    let method = 'POST';
    let url = `${config.backendUrl}/api/credit_requests?access_token=${accessToken}`;
    if (creditInfo && creditInfo.id && creditInfo.status !== 'approved') {
      method = 'PATCH';
      url = `${config.backendUrl}/api/credit_requests/${creditInfo.id}?access_token=${accessToken}`;
      creditRequestData = {
        amount: amountInput,
      };
    }

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(creditRequestData),
      });

      const responseData = await response.json();

      if (response.ok) {
        setCreditInfo(responseData.data[0])
        toast({
          title: t('myCredit.creditRequestSubmitted'),
          description: t('myCredit.creditRequestDescription', { amount: formatCurrencyAmount(responseData.data[0].amount, currency) }),
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('myCredit.error'),
          description: responseData.title || t('myCredit.errorSubmittingCreditRequest'),
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: t('myCredit.error'),
        description: error.message || t('myCredit.generalError'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  let formHeading = t('myCredit.formHeading');
  let buttonLabel = t('myCredit.buttonLabel');
  let creditStatusMessage = t('myCredit.creditStatusMessage');
  let creditStatus = 'info';

  if (creditInfo && creditInfo.status === 'pending') {
    formHeading = t('myCredit.pendingFormHeading');
    buttonLabel = t('myCredit.pendingButtonLabel');
    creditStatusMessage = t('myCredit.pendingCreditStatusMessage', { amount: formatCurrencyAmount(creditInfo.amount, currency) });
  }

  if (creditInfo && creditInfo.status === 'approved') {
    creditStatus = 'success';
    formHeading = t('myCredit.approvedFormHeading');
    buttonLabel = t('myCredit.approvedButtonLabel');
    if (approvedAmount === '-1') {
      creditStatusMessage = t('myCredit.approvedCreditStatusMessageUnlimited');
    } else {
      const requestedAmount = creditInfo.amount;
      const currentCredit = bidderInfo.live_credit;
      
      if (requestedAmount !== currentCredit) {
        creditStatusMessage = t('myCredit.approvedCreditStatusMessageModified', { 
          amount_request: formatCurrencyAmount(requestedAmount, currency),
          current_amount: formatCurrencyAmount(currentCredit, currency)
        });
      } else {
        creditStatusMessage = t('myCredit.approvedCreditStatusMessage', { 
          amount_request: formatCurrencyAmount(requestedAmount, currency)
        });
      }
    }
  }

  const renderContent = () => {
    if (isLoading) {
      return <Text>{t('myCredit.loading')}</Text>;
    }

    if (creditInfo && creditInfo.status === 'rejected') {
      return (
        <Box p={4} borderWidth="1px" borderRadius="lg">
          <div className="bidderNumber">
            <HStack>
              <Icon as={FaLaptop} mr={2} />
              <Box>{t('myCredit.bidderNumber', { number: bidderInfo.bidder_number })}</Box>
            </HStack>
          </div>

          <Alert status="error" mb={4}>
            <AlertIcon />
            <AlertDescription>
              {approvedAmount === '-1' 
                ? t('myCredit.rejectedCreditStatusMessageUnlimited')
                : t('myCredit.rejectedCreditStatusMessage', { 
                amount: formatCurrencyAmount(approvedAmount, currency) 
              })}
            </AlertDescription>
          </Alert>
        </Box>
      );
    }

    if (approvedAmount && !creditInfo) {
      return (
        <Box p={4} borderWidth="1px" borderRadius="lg">
          <div className="bidderNumber">
            <HStack>
              <Icon as={FaLaptop} mr={2} />
              <Box>{t('myCredit.bidderNumber', {number: bidderInfo.bidder_number})}</Box>
            </HStack>
          </div>            
          <Alert status="success" mb={4}>
            <AlertIcon />
            <AlertDescription>
              {approvedAmount === '-1'
                ? t('myCredit.approvedCreditStatusMessageUnlimited')
                : t('myCredit.approvedCreditStatusMessageNoRequest', { amount: formatCurrencyAmount(approvedAmount, currency) })}
            </AlertDescription>
          </Alert>
  
          {approvedAmount !== '-1' && (
            <form onSubmit={handleSubmit}>
              <Heading as="h3" size="sm" mb={4}>{formHeading}</Heading>
              <Box className="instructions" mb={4}>
                <Text size="xs">{t('myCredit.instructions')}</Text>
                <Text size="xs">{t('myCredit.noCommasOrDecimals')}</Text>
              </Box>
              <FormControl id="credit-request" isRequired>
                <FormLabel>{t('myCredit.creditRequestLabel', {currency})}</FormLabel>
                <Input
                  type="number"
                  min="1"
                  max="2147483647"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                />
                <Button colorScheme="blue" mt={4} isLoading={isSubmitting} type="submit">
                  {buttonLabel}
                </Button>
              </FormControl>
            </form>
          )}
        </Box>
      );
    }

    return (
      <Box p={4} borderWidth="1px" borderRadius="lg">
        <div className="bidderNumber">
          <HStack>
            <Icon as={FaLaptop} mr={2} />
            <Box>{t('myCredit.bidderNumber', {number: bidderInfo.bidder_number})}</Box>
          </HStack>
        </div>          
        <Alert status={creditInfo?.status === 'approved' ? 'success' : 'info'} mb={4}>
          <AlertIcon />
          <AlertDescription>
            {approvedAmount === '-1' 
              ? t('myCredit.approvedCreditStatusMessageUnlimited')
              : t('myCredit.approvedCreditStatusMessageNoRequest', { amount: formatCurrencyAmount(approvedAmount, currency) })}
          </AlertDescription>
        </Alert>

        {approvedAmount !== '-1' && (
          <form onSubmit={handleSubmit}>
            <Heading as="h3" size="sm" mb={4}>{formHeading}</Heading>
            <Box className="instructions" mb={4}>
              <Text size="xs">{t('myCredit.instructions')}</Text>
              <Text size="xs">{t('myCredit.noCommasOrDecimals')}</Text>
            </Box>
            
            {creditInfo && (
              <Alert status={creditStatus} mb={4}>
                <AlertIcon/>
                <AlertDescription>{creditStatusMessage}</AlertDescription>
              </Alert>
            )}
            
            <FormControl id="credit-request" isRequired>
              <FormLabel>{t('myCredit.creditRequestLabel', {currency})}</FormLabel>
              <Input
                type="number"
                min="1"
                max="2147483647"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
              />
              <Button 
                colorScheme="blue" 
                mt={4} 
                isLoading={isSubmitting} 
                type="submit"
                isDisabled={!isAmountChanged}
              >
                {buttonLabel}
              </Button>
            </FormControl>
          </form>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        {t('myCredit.pageTitle')}
      </Heading>
      {bidderInfo && renderContent()}
    </Box>
  );
};

export default MyCredit;